import { useEffect, useState } from "react";
import {
  LayoutDashboard, Megaphone, FolderOpen,
  MessageSquare, LogOut, Users, Settings,
  Info, UserPlus, Globe, Gift, Phone, PanelTop,
  Plus, Settings2, Check, X, ExternalLink, ChevronRight,
  MapPin, Activity, Sparkles, Radar,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { useWahaSession } from "@/hooks/use-waha-session";
import { useBriefingActionCount } from "@/hooks/use-briefing-action-count";
import { useNeedsZoneRefine } from "@/hooks/use-needs-zone-refine";
import { useJulietteLauncher } from "@/hooks/use-juliette-launcher";
import { useJulietteActiveSession } from "@/hooks/use-juliette-active-session";
import { useTeamId } from "@/hooks/use-team-id";
import { listJulietteSignals } from "@/lib/juliette-signals";
import { computeJulietteWhisper } from "@/lib/juliette-whisper";
import { useSignalsClearedFlash } from "@/hooks/use-signals-cleared-flash";
import { JulietteSidebarPresence } from "@/components/juliette/JulietteSidebarPresence";
import { GuidanceNudgeBubble } from "@/components/juliette/GuidanceNudgeBubble";
import { NavLink } from "@/components/NavLink";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { S1Logo } from "@/components/S1Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AddContactDialog } from "@/components/shared/AddContactDialog";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarHeader,
  SidebarFooter,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";

// ─── Navigation ───────────────────────────────────────────────────────────────

type NavChild =
  | { title: string; url: string; end?: boolean; action?: never }
  | { title: string; action: () => void; url?: never; end?: never };

type NavItem = {
  title: string;
  shortLabel: string;
  url: string;
  icon: typeof LayoutDashboard;
  tourStep?: string;
  children?: NavChild[];
  /** Si défini, l'item agit comme un bouton (ouvre un drawer/dialog) au lieu d'une route. */
  action?: () => void;
};

const ALL_NAV_ITEMS: NavItem[] = [
  { title: "Tableau de bord",    shortLabel: "Dashboard", url: "/",            icon: LayoutDashboard, tourStep: "nav-dashboard" },
  { title: "Juliette",           shortLabel: "Juliette",  url: "/settings/juliette", icon: Sparkles,  tourStep: "nav-juliette" },
  {
    title: "Prospection",
    shortLabel: "Prospect.",
    url: "/prospection",
    icon: UserPlus,
    tourStep: "nav-prospection",
    children: [
      { title: "Terrain humain",       url: "/prospection/terrain" },
      { title: "Cadastral automatisé", url: "/prospection/cadastral" },
    ],
  },
  { title: "CRM",                shortLabel: "CRM",       url: "/crm",         icon: Users,           tourStep: "nav-crm" },
  { title: "Messagerie",         shortLabel: "Messages",  url: "/messages",    icon: MessageSquare,   tourStep: "nav-messages" },
  { title: "Bibliothèque",          shortLabel: "Biblio",      url: "/media",       icon: FolderOpen,      tourStep: "nav-media" },
  { title: "Site web",           shortLabel: "Site",      url: "/vitrine",     icon: PanelTop,        tourStep: "nav-vitrine" },
  { title: "Ambassadeurs",       shortLabel: "Parrain.",  url: "/referral",    icon: Gift,            tourStep: "nav-referral" },
  {
    title: "Campagnes",
    shortLabel: "Campagnes",
    url: "/campaigns",
    icon: Megaphone,
    tourStep: "nav-campaigns",
    children: [
      { title: "Pilotage",  url: "/campaigns",           end: true },
      { title: "Campagnes", url: "/campaigns/templates" },
      { title: "Branding",  url: "/campaigns/branding" },
    ],
  },
  { title: "Sniper Web",         shortLabel: "Annonces",  url: "/scraping",    icon: Globe,        tourStep: "nav-scraping" },
  { title: "Visibilité locale",  shortLabel: "Visibilité", url: "/visibilite", icon: Radar,        tourStep: "nav-visibilite" },
  { title: "Paramètres",         shortLabel: "Config.",   url: "/settings",    icon: Settings },
];

// Items affichés dans la nav principale (Paramètres reste dans le footer uniquement)
const MAIN_NAV_ITEMS = ALL_NAV_ITEMS.filter((n) => n.url !== "/settings");

// Liste mobile dediee : seuls les onglets qui ne sont pas deja dans la
// bottom nav (Accueil, Messages, Sniper) ni dans le bouton CTA central
// (orb Juliette V3.5). On promeut "Terrain humain" et "Cadastre" en items
// top-niveau pour un acces direct depuis le menu burger.
const MOBILE_NAV_ITEMS: NavItem[] = [
  { title: "Juliette",       shortLabel: "Juliette", url: "/settings/juliette",     icon: Sparkles },
  { title: "Terrain humain", shortLabel: "Terrain",  url: "/prospection/terrain",   icon: MapPin },
  { title: "Cadastre",       shortLabel: "Cadastre", url: "/prospection/cadastral", icon: UserPlus },
  { title: "CRM",            shortLabel: "CRM",      url: "/crm",                   icon: Users },
  { title: "Bibliothèque",      shortLabel: "Biblio",     url: "/media",                 icon: FolderOpen },
  { title: "Site web",       shortLabel: "Site",     url: "/vitrine",               icon: PanelTop },
  { title: "Ambassadeurs",   shortLabel: "Parrain.", url: "/referral",              icon: Gift },
  { title: "Campagnes",      shortLabel: "Campagnes",url: "/campaigns",             icon: Megaphone },
  { title: "Visibilité",     shortLabel: "Visibilité",url: "/visibilite",           icon: Radar },
];

// Item réservé au super-admin (frednordimmo@gmail.com) : monitoring Juliette.
// Volontairement hors de ALL_NAV_ITEMS pour qu'il ne fuite jamais aux autres comptes.
const JULIETTE_ADMIN_ITEM: NavItem = {
  title: "Santé Juliette",
  shortLabel: "Juliette",
  url: "/admin/juliette-health",
  icon: Activity,
};

// ─── Raccourcis (localStorage) ───────────────────────────────────────────────

type ShortcutEntry = {
  url: string;        // route interne (commence par "/") ou URL externe (https://...)
  label?: string;     // pour les externes : label affiche
  external?: boolean; // true = lien externe, ouvre dans un nouvel onglet
};

const SHORTCUTS_STORAGE_KEY = "s1-sidebar-shortcuts";
const DEFAULT_SHORTCUTS: ShortcutEntry[] = [
  { url: "/campaigns" },
  { url: "/scraping" },
];
const MAX_SHORTCUTS = 12;

const SHORTCUT_COLORS: Record<string, string> = {
  "/campaigns":   "bg-orange-500/15 text-orange-700 dark:text-orange-300",
  "/scraping":    "bg-sky-500/15 text-sky-700 dark:text-sky-300",
  "/visibilite":  "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300",
  "/vitrine":     "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  "/media":       "bg-violet-500/15 text-violet-700 dark:text-violet-300",
  "/referral":    "bg-pink-500/15 text-pink-700 dark:text-pink-300",
  "/messages":    "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  "/crm":         "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300",
  "/prospection": "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  "/":            "bg-teal-500/15 text-teal-700 dark:text-teal-300",
  "/settings":    "bg-slate-500/15 text-slate-700 dark:text-slate-300",
};

function loadShortcuts(): ShortcutEntry[] {
  if (typeof window === "undefined") return DEFAULT_SHORTCUTS;
  try {
    const stored = window.localStorage.getItem(SHORTCUTS_STORAGE_KEY);
    if (!stored) return DEFAULT_SHORTCUTS;
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return DEFAULT_SHORTCUTS;

    return parsed
      .map((entry): ShortcutEntry | null => {
        // backward compat : ancien format string[] -> { url }
        if (typeof entry === "string") return { url: entry };
        if (entry && typeof entry === "object" && typeof entry.url === "string") {
          return {
            url: entry.url,
            label: typeof entry.label === "string" ? entry.label : undefined,
            external: entry.external === true,
          };
        }
        return null;
      })
      .filter((s): s is ShortcutEntry => s !== null);
  } catch {
    return DEFAULT_SHORTCUTS;
  }
}

function persistShortcuts(next: ShortcutEntry[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SHORTCUTS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // localStorage indisponible — silently fail
  }
}

function normalizeExternalUrl(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  // si l'utilisateur n'a pas mis de schema, on prefixe https://
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const u = new URL(withScheme);
    if (!u.hostname.includes(".")) return null;
    return u.toString();
  } catch {
    return null;
  }
}

function getFaviconUrl(url: string): string | null {
  try {
    const u = new URL(url);
    return `https://www.google.com/s2/favicons?domain=${u.hostname}&sz=64`;
  } catch {
    return null;
  }
}

function getDefaultExternalLabel(url: string): string {
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./, "").split(".")[0];
  } catch {
    return "Lien";
  }
}

// ─── Item de navigation principale (avec sous-menu collapsible) ──────────────

const NAV_ITEM_BASE_CLASS = `
  flex items-center gap-3 mx-1 px-3 py-3 rounded-xl
  text-sm text-sidebar-foreground/75 dark:text-sidebar-foreground/60
  hover:bg-sidebar-accent/60 hover:text-sidebar-foreground
  transition-all duration-150
  group-data-[collapsible=icon]:justify-center
  group-data-[collapsible=icon]:px-0
  group-data-[collapsible=icon]:mx-0
  group-data-[collapsible=icon]:py-2
`;

const NAV_ITEM_ACTIVE_CLASS = `
  bg-sidebar-accent text-sidebar-accent-foreground font-medium
  border border-sidebar-border
  dark:bg-gradient-to-r dark:from-indigo-500/20 dark:via-indigo-500/10 dark:to-purple-500/10
  dark:border-indigo-500/25
  dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.06),_0_0_18px_rgba(99,102,241,0.18)]
`;

function MainNavItem({
  item,
  briefingCount,
  needsZoneRefine,
  closeMobileSidebar,
}: {
  item: NavItem;
  briefingCount: number;
  needsZoneRefine: boolean;
  closeMobileSidebar: () => void;
}) {
  const { pathname } = useLocation();
  const hasChildren = !!item.children?.length;
  const isOnSection = hasChildren && pathname.startsWith(item.url);
  const [open, setOpen] = useState(isOnSection);

  // Ouvre le sous-menu quand on arrive sur une route enfant via lien externe.
  useEffect(() => {
    if (isOnSection) setOpen(true);
  }, [isOnSection]);

  if (!hasChildren) {
    // Cas "action" : bouton qui ouvre un drawer/dialog (ex : Carnet Terrain).
    if (item.action) {
      const handleClick = () => {
        closeMobileSidebar();
        item.action!();
      };
      return (
        <SidebarMenuItem>
          <button
            type="button"
            onClick={handleClick}
            className={cn(NAV_ITEM_BASE_CLASS, "w-[calc(100%-0.5rem)] text-left")}
            {...(item.tourStep ? { "data-tour-step": item.tourStep } : {})}
          >
            <item.icon className="h-4 w-4 shrink-0" />
            <span className="group-data-[collapsible=icon]:hidden">{item.title}</span>
          </button>
        </SidebarMenuItem>
      );
    }

    // Sniper Web : tant que la zone n'est pas affinée, on pointe direct sur
    // l'onglet « Zones de prospection » et on affiche une pastille de rappel.
    const showZoneRefine = item.url === "/scraping" && needsZoneRefine;
    const to = showZoneRefine ? "/scraping?tab=zones" : item.url;

    return (
      <SidebarMenuItem>
        <NavLink
          to={to}
          end={item.url === "/"}
          onClick={closeMobileSidebar}
          className={cn(NAV_ITEM_BASE_CLASS, showZoneRefine && "relative")}
          activeClassName={NAV_ITEM_ACTIVE_CLASS}
          {...(item.tourStep ? { "data-tour-step": item.tourStep } : {})}
        >
          <item.icon className="h-4 w-4 shrink-0" />
          <span className="group-data-[collapsible=icon]:hidden">{item.title}</span>
          {item.url === "/messages" && briefingCount > 0 && (
            <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white group-data-[collapsible=icon]:hidden">
              {briefingCount > 99 ? "99+" : briefingCount}
            </span>
          )}
          {showZoneRefine && (
            <span
              aria-label="Zone de prospection à affiner"
              className="ml-auto h-2 w-2 shrink-0 rounded-full bg-warning shadow-[0_0_0_3px_hsl(var(--warning)/0.2)] group-data-[collapsible=icon]:absolute group-data-[collapsible=icon]:top-1.5 group-data-[collapsible=icon]:right-1.5 group-data-[collapsible=icon]:ml-0"
            />
          )}
        </NavLink>
      </SidebarMenuItem>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className={cn(NAV_ITEM_BASE_CLASS, "w-[calc(100%-0.5rem)] text-left", isOnSection && NAV_ITEM_ACTIVE_CLASS)}
            {...(item.tourStep ? { "data-tour-step": item.tourStep } : {})}
          >
            <item.icon className="h-4 w-4 shrink-0" />
            <span className="group-data-[collapsible=icon]:hidden">{item.title}</span>
            <ChevronRight
              className={cn(
                "ml-auto h-3.5 w-3.5 shrink-0 text-sidebar-foreground/65 dark:text-sidebar-foreground/40 transition-transform duration-200 group-data-[collapsible=icon]:hidden",
                open && "rotate-90",
              )}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub className="mt-1 mb-1">
            {item.children!.map((child) => {
              const key = child.action ? `action:${child.title}` : child.url;
              if (child.action) {
                const handleChildClick = () => {
                  closeMobileSidebar();
                  child.action!();
                };
                return (
                  <SidebarMenuSubItem key={key}>
                    <SidebarMenuSubButton asChild>
                      <button
                        type="button"
                        onClick={handleChildClick}
                        className="text-sidebar-foreground/55 hover:text-sidebar-foreground transition-colors w-full text-left"
                      >
                        <span>{child.title}</span>
                      </button>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                );
              }
              return (
                <SidebarMenuSubItem key={key}>
                  <SidebarMenuSubButton asChild>
                    <NavLink
                      to={child.url}
                      end={child.end}
                      onClick={closeMobileSidebar}
                      className="text-sidebar-foreground/55 hover:text-sidebar-foreground transition-colors"
                      activeClassName="text-sidebar-foreground font-medium bg-sidebar-accent"
                    >
                      <span>{child.title}</span>
                    </NavLink>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(
  fullName: string | null | undefined,
  email: string | null | undefined
): string {
  if (fullName) {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length >= 2)
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return fullName.substring(0, 2).toUpperCase();
  }
  if (email) return email.substring(0, 2).toUpperCase();
  return "??";
}

function getDisplayName(
  fullName: string | null | undefined,
  email: string | null | undefined
): string {
  if (fullName) return fullName;
  if (email) return email.split("@")[0];
  return "Utilisateur";
}

// ─── Composant ────────────────────────────────────────────────────────────────

export function AppSidebar() {
  const { signOut, user } = useAuth();
  const navigate = useNavigate();
  const { setOpenMobile, isMobile, state: sidebarState } = useSidebar();
  const { open: openJuliette, context: julietteContext } = useJulietteLauncher();
  const { data: julietteSession } = useJulietteActiveSession();
  const { teamId } = useTeamId();

  // Signaux : on fetch PARTOUT (meme la couleur de l'orbe doit reagir des le
  // dashboard). Meme cle de cache que JulietteSignalsList → quand l'agent
  // traite/ecarte un signal dans le panneau, le compteur decremente tout seul
  // (cache partage invalide par la liste). La PHRASE (whisper) reste reservee
  // aux ecrans proactifs (CRM / Messagerie) pour ne pas doublonner le briefing.
  const { data: signals = [] } = useQuery({
    queryKey: ["juliette-signals", teamId],
    enabled: !!teamId,
    queryFn: () => listJulietteSignals(),
    staleTime: 60_000,
  });
  const whisper = computeJulietteWhisper(signals, julietteContext.proactive);
  // Flash vert « fait » quand l'agent vient de tout traiter (compteur → 0).
  const justCleared = useSignalsClearedFlash(whisper.count, julietteContext.proactive);

  const julietteStatusLabel =
    julietteContext.role === "analyste"
      ? "Analyste · prête"
      : julietteContext.role === "reponse"
        ? "Assistante · prête"
        : "Je veille";

  // Monitoring Juliette : visible uniquement pour le super-admin.
  const isSuperadmin = user?.email === "frednordimmo@gmail.com";
  const navItems = isSuperadmin
    ? [...(isMobile ? MOBILE_NAV_ITEMS : MAIN_NAV_ITEMS), JULIETTE_ADMIN_ITEM]
    : isMobile
      ? MOBILE_NAV_ITEMS
      : MAIN_NAV_ITEMS;

  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [shortcuts, setShortcuts] = useState<ShortcutEntry[]>(loadShortcuts);
  const [editShortcuts, setEditShortcuts] = useState(false);
  const [shortcutDialogOpen, setShortcutDialogOpen] = useState(false);
  const [externalUrl, setExternalUrl] = useState("");
  const [externalLabel, setExternalLabel] = useState("");
  const [externalError, setExternalError] = useState<string | null>(null);

  const closeMobileSidebar = () => {
    if (isMobile) setOpenMobile(false);
  };

  const saveShortcuts = (next: ShortcutEntry[]) => {
    setShortcuts(next);
    persistShortcuts(next);
  };

  const resetExternalForm = () => {
    setExternalUrl("");
    setExternalLabel("");
    setExternalError(null);
  };

  const handleAddInternalShortcut = (url: string) => {
    saveShortcuts([...shortcuts, { url }]);
    setShortcutDialogOpen(false);
  };

  const handleAddExternalShortcut = () => {
    const normalized = normalizeExternalUrl(externalUrl);
    if (!normalized) {
      setExternalError("URL invalide. Exemple : leboncoin.fr ou https://chatgpt.com");
      return;
    }
    const label = externalLabel.trim() || getDefaultExternalLabel(normalized);
    saveShortcuts([...shortcuts, { url: normalized, label, external: true }]);
    setShortcutDialogOpen(false);
    resetExternalForm();
  };

  const fullName = user?.user_metadata?.full_name as string | undefined;
  const email = user?.email;
  const initials = getInitials(fullName, email);
  const displayName = getDisplayName(fullName, email);
  const { data: wahaSession } = useWahaSession();

  const { data: briefingCount = 0 } = useBriefingActionCount();
  const needsZoneRefine = useNeedsZoneRefine();

  const { data: avatarUrl } = useQuery({
    queryKey: ["profile-avatar", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("avatar_url")
        .eq("user_id", user!.id)
        .single();
      return data?.avatar_url ?? null;
    },
    enabled: !!user?.id,
  });

  const handleLogout = async () => {
    await signOut();
    navigate("/login");
  };

  const availableForShortcuts = ALL_NAV_ITEMS.filter(
    (n) => !shortcuts.some((s) => s.url === n.url)
  );

  return (
    <>
      <Sidebar
        collapsible="icon"
        className="border-r border-sidebar-border [&_[data-sidebar=sidebar]]:bg-[linear-gradient(to_bottom,hsl(218_69%_12%)_0%,hsl(218_69%_12%)_10%,hsl(220_30%_96%)_22%,hsl(220_30%_96%)_78%,hsl(218_69%_12%)_90%,hsl(218_69%_12%)_100%)] dark:[&_[data-sidebar=sidebar]]:bg-none"
      >
        {/* ─── Header (zone foncée en light) ───────────────────────────────── */}
        <SidebarHeader className="px-4 py-5 group-data-[collapsible=icon]:px-2 [--sidebar-foreground:0_0%_88%] dark:[--sidebar-foreground:220_9%_70%]">
          <div className="flex items-center justify-between group-data-[collapsible=icon]:justify-center">
            <div className="flex items-center group-data-[collapsible=icon]:hidden">
              <S1Logo variant="full" height={36} />
            </div>
            <SidebarTrigger className="text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/40 transition-colors" />
          </div>
        </SidebarHeader>

        <SidebarContent className="gap-2">
          {/* ─── Bouton Nouveau lead ───────────────────────────────────── */}
          <div className="px-3 pt-1 group-data-[collapsible=icon]:px-1">
            <button
              onClick={() => setAddLeadOpen(true)}
              className="
                group/lead w-full flex items-center justify-center gap-2
                rounded-xl px-4 py-2.5
                bg-gradient-to-br from-indigo-500 via-indigo-500 to-purple-600
                text-white font-semibold text-sm
                shadow-[0_4px_14px_rgba(99,102,241,0.35),_inset_0_1px_0_rgba(255,255,255,0.2)]
                dark:shadow-[0_0_22px_rgba(99,102,241,0.45),_0_4px_14px_rgba(0,0,0,0.35),_inset_0_1px_0_rgba(255,255,255,0.2)]
                hover:shadow-[0_6px_18px_rgba(99,102,241,0.45),_inset_0_1px_0_rgba(255,255,255,0.25)]
                dark:hover:shadow-[0_0_32px_rgba(99,102,241,0.65),_0_6px_18px_rgba(0,0,0,0.45),_inset_0_1px_0_rgba(255,255,255,0.25)]
                hover:-translate-y-[1px] active:translate-y-0
                border border-white/15
                transition-all duration-200
                group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:rounded-lg
              "
              title="Nouveau lead"
              aria-label="Nouveau lead"
              data-tour-step="new-lead-btn"
            >
              <Plus className="h-4 w-4 shrink-0" />
              <span className="group-data-[collapsible=icon]:hidden">Nouveau lead</span>
            </button>
          </div>

          {/* ─── Navigation principale ──────────────────────────────────── */}
          <SidebarGroup className="pt-2">
            <SidebarGroupContent>
              <SidebarMenu className="gap-1">
                {navItems.map((item) => (
                  <MainNavItem
                    key={item.action ? `action:${item.title}` : item.url}
                    item={item}
                    briefingCount={briefingCount}
                    needsZoneRefine={needsZoneRefine}
                    closeMobileSidebar={closeMobileSidebar}
                  />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* ─── Raccourcis personnalisables ────────────────────────────── */}
          <SidebarGroup className="group-data-[collapsible=icon]:hidden mt-1">
            <div className="flex items-center justify-between px-4 mb-1.5">
              <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-sidebar-foreground/55 dark:text-sidebar-foreground/30">
                Raccourcis
              </span>
              {editShortcuts ? (
                <button
                  onClick={() => setEditShortcuts(false)}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-700 dark:text-indigo-400 text-[10px] font-semibold hover:bg-indigo-500/30 transition-colors"
                  aria-label="Terminer"
                >
                  <Check className="h-2.5 w-2.5" />
                  OK
                </button>
              ) : (
                <button
                  onClick={() => setEditShortcuts(true)}
                  className="-my-1 inline-flex size-9 items-center justify-center rounded-md text-sidebar-foreground/60 dark:text-sidebar-foreground/50 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors"
                  title="Personnaliser les raccourcis"
                  aria-label="Personnaliser les raccourcis"
                >
                  <Settings2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <div className="flex flex-row flex-wrap gap-1.5 px-3">
              {shortcuts.map((sc, idx) => {
                const isExternal = sc.external === true;
                const internalItem = isExternal ? null : ALL_NAV_ITEMS.find((n) => n.url === sc.url);
                const colorCls = isExternal
                  ? "bg-sidebar-foreground/10 text-sidebar-foreground/70"
                  : (SHORTCUT_COLORS[sc.url] ?? "bg-sidebar-foreground/10 text-sidebar-foreground/75 dark:text-sidebar-foreground/60");
                const label = isExternal
                  ? (sc.label ?? getDefaultExternalLabel(sc.url))
                  : (internalItem?.shortLabel ?? "?");
                const tooltip = isExternal ? `${label} — ${sc.url}` : (internalItem?.title ?? sc.url);
                const faviconUrl = isExternal ? getFaviconUrl(sc.url) : null;

                const tileInner = (
                  <>
                    {isExternal && faviconUrl ? (
                      <img
                        src={faviconUrl}
                        alt=""
                        className="h-4 w-4 rounded-sm"
                        onError={(e) => {
                          // fallback : remplacer par l'icone Globe via masquage de l'image
                          e.currentTarget.style.display = "none";
                          const sibling = e.currentTarget.nextElementSibling as HTMLElement | null;
                          if (sibling) sibling.style.display = "";
                        }}
                      />
                    ) : null}
                    {isExternal && (
                      <ExternalLink
                        className="h-4 w-4"
                        style={{ display: faviconUrl ? "none" : undefined }}
                      />
                    )}
                    {!isExternal && internalItem && <internalItem.icon className="h-4 w-4" />}
                    <span className="truncate max-w-[42px] text-center">{label}</span>
                    {editShortcuts && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          saveShortcuts(shortcuts.filter((_, i) => i !== idx));
                        }}
                        className="absolute -top-1 -right-1 h-3.5 w-3.5 bg-red-500 rounded-full flex items-center justify-center shadow-md hover:scale-110 transition-transform"
                        title="Retirer"
                        aria-label="Retirer ce raccourci"
                      >
                        <X className="h-2 w-2 text-white" strokeWidth={3} />
                      </button>
                    )}
                  </>
                );

                const tileClass = `
                  relative flex flex-col items-center justify-center gap-0.5
                  w-12 h-12 rounded-xl
                  ${colorCls}
                  hover:brightness-125
                  transition-all duration-150
                  text-[9px] font-medium leading-tight
                `;

                if (isExternal) {
                  return (
                    <a
                      key={`${sc.url}-${idx}`}
                      href={sc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={tileClass}
                      title={tooltip}
                    >
                      {tileInner}
                    </a>
                  );
                }

                return (
                  <NavLink
                    key={`${sc.url}-${idx}`}
                    to={sc.url}
                    end={sc.url === "/"}
                    onClick={closeMobileSidebar}
                    className={tileClass}
                    activeClassName="ring-1 ring-sidebar-foreground/20"
                    title={tooltip}
                  >
                    {tileInner}
                  </NavLink>
                );
              })}

              {editShortcuts && shortcuts.length < MAX_SHORTCUTS && (
                <button
                  onClick={() => { resetExternalForm(); setShortcutDialogOpen(true); }}
                  className="
                    flex flex-col items-center justify-center gap-0.5
                    w-12 h-12 rounded-xl
                    border border-dashed border-sidebar-foreground/15
                    text-sidebar-foreground/55 dark:text-sidebar-foreground/30
                    hover:border-indigo-500/40 hover:text-indigo-700/80 dark:hover:text-indigo-400/60
                    transition-all duration-150
                    text-[9px] font-medium
                  "
                  title="Ajouter un raccourci"
                  aria-label="Ajouter un raccourci"
                >
                  <Plus className="h-4 w-4" />
                  <span>Ajouter</span>
                </button>
              )}
            </div>

            {shortcuts.length === 0 && !editShortcuts && (
              <div className="px-4 py-1">
                <button
                  onClick={() => setEditShortcuts(true)}
                  className="text-[10px] text-sidebar-foreground/55 dark:text-sidebar-foreground/30 hover:text-sidebar-foreground/60 transition-colors"
                >
                  + Ajouter un raccourci
                </button>
              </div>
            )}
          </SidebarGroup>

        </SidebarContent>

        {/* ─── Footer ─────────────────────────────────────────────────────── */}
        <SidebarFooter className="px-3 py-3 border-t border-white/10 dark:border-sidebar-border [--sidebar-foreground:0_0%_88%] dark:[--sidebar-foreground:220_9%_70%]">
          {/* ─── Présence Juliette (ancrage permanent, toujours visible) ─── */}
          <div className="relative mb-2 border-b border-white/10 pb-2 group-data-[collapsible=icon]:border-b-0 group-data-[collapsible=icon]:pb-1">
            {/* Coup de pouce d'accompagnement : émane de l'orbe, sur desktop ET
                tablette en mode rail (sinon la tablette ne verrait aucun conseil,
                la bottom-nav étant masquée au-delà de 768px). */}
            <GuidanceNudgeBubble placement="sidebar" />
            <JulietteSidebarPresence
              statusLabel={julietteStatusLabel}
              hasPending={!!julietteSession}
              signalCount={whisper.count}
              whisper={whisper.active ? { headline: whisper.headline } : null}
              done={justCleared}
              compact={sidebarState === "collapsed"}
              onOpen={() => {
                closeMobileSidebar();
                // Des signaux en attente → ouvre direct l'onglet Signaux. Sinon Échange.
                openJuliette(whisper.count > 0 ? { tab: "signaux" } : undefined);
              }}
            />
          </div>
          <div className="flex items-center gap-2 px-1 mb-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
            <Avatar className="h-9 w-9 shrink-0">
              {avatarUrl && <AvatarImage src={avatarUrl} alt={displayName} />}
              <AvatarFallback className="bg-sidebar-primary text-sidebar-primary-foreground text-xs font-semibold select-none">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1 group-data-[collapsible=icon]:hidden">
              <p className="text-sm font-semibold leading-tight text-sidebar-foreground truncate" title={displayName}>{displayName}</p>
              {email && (
                <p className="text-[11px] leading-tight text-sidebar-foreground/75 dark:text-sidebar-foreground/60 truncate" title={email}>{email}</p>
              )}
              {wahaSession?.status === "WORKING" && (
                <button
                  onClick={() => { closeMobileSidebar(); navigate("/settings"); }}
                  className="flex items-center gap-1 min-w-0 w-full min-h-[44px] md:min-h-[32px] hover:opacity-80 transition-opacity cursor-pointer"
                  title="Configurer WhatsApp"
                >
                  <svg viewBox="0 0 24 24" className="h-3 w-3 shrink-0 fill-[#25D366]" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span className="text-[11px] text-[#25D366] truncate flex-1 min-w-0 text-left">
                    {wahaSession.phone
                      ? (wahaSession.phone.startsWith("33") ? "0" + wahaSession.phone.slice(2) : wahaSession.phone)
                      : "Connecté"}
                  </span>
                </button>
              )}
              {wahaSession && wahaSession.status !== "WORKING" && (
                <button
                  onClick={() => { closeMobileSidebar(); navigate("/settings"); }}
                  className="flex items-center gap-1 min-w-0 w-full min-h-[44px] md:min-h-[32px] hover:opacity-80 transition-opacity cursor-pointer"
                  title="Reconnecter WhatsApp"
                >
                  <Phone className="h-3 w-3 shrink-0 text-amber-600 dark:text-amber-400" />
                  <span className="text-[11px] text-amber-600 dark:text-amber-400 truncate flex-1 min-w-0 text-left">WhatsApp KO</span>
                </button>
              )}
              {!wahaSession && (
                <button
                  onClick={() => { closeMobileSidebar(); navigate("/settings"); }}
                  className="flex items-center gap-1 min-w-0 w-full min-h-[44px] md:min-h-[32px] hover:underline cursor-pointer"
                  title="Configurer WhatsApp"
                >
                  <Phone className="h-3 w-3 shrink-0 text-sidebar-foreground/65 dark:text-sidebar-foreground/40" />
                  <span className="text-[11px] text-sidebar-foreground/65 dark:text-sidebar-foreground/40 truncate flex-1 min-w-0 text-left">Configurer WhatsApp</span>
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1 px-1 group-data-[collapsible=icon]:flex-col">
            <ThemeToggle />
            <button
              onClick={() => { closeMobileSidebar(); navigate("/settings"); }}
              title="Paramètres"
              className="flex h-10 w-10 md:h-8 md:w-8 items-center justify-center rounded-lg text-sidebar-foreground/75 dark:text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors"
            >
              <Settings className="h-4 w-4" />
            </button>
            <button
              onClick={() => { closeMobileSidebar(); navigate("/compliance"); }}
              title="Conformité & Légal"
              className="flex h-10 w-10 md:h-8 md:w-8 items-center justify-center rounded-lg text-sidebar-foreground/75 dark:text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors"
            >
              <Info className="h-4 w-4" />
            </button>
            <button
              onClick={handleLogout}
              title="Se déconnecter"
              className="flex h-10 w-10 md:h-8 md:w-8 items-center justify-center rounded-lg text-sidebar-foreground/75 dark:text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors cursor-pointer"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </SidebarFooter>
      </Sidebar>

      {/* Dialog "Nouveau lead" */}
      <AddContactDialog
        open={addLeadOpen}
        onOpenChange={setAddLeadOpen}
        defaults={{ lead_source: "crm", entry_campaign: "lead_crm_actif" }}
      />

      {/* Dialog "Ajouter un raccourci" */}
      <Dialog
        open={shortcutDialogOpen}
        onOpenChange={(open) => {
          setShortcutDialogOpen(open);
          if (!open) resetExternalForm();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter un raccourci</DialogTitle>
            <DialogDescription>
              Ajoute une page de l'app, ou n'importe quel site externe (LeBonCoin, ChatGPT, ta boîte mail, etc.).
              Tes raccourcis restent privés et stockés sur ton navigateur.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Pages de l'app */}
            {availableForShortcuts.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">
                  Pages de l'app
                </p>
                <div className="grid grid-cols-2 gap-1.5">
                  {availableForShortcuts.map((n) => (
                    <button
                      key={n.url}
                      onClick={() => handleAddInternalShortcut(n.url)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border hover:bg-accent hover:border-primary/40 text-sm text-foreground/80 hover:text-foreground transition-colors text-left"
                    >
                      <n.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate">{n.title}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Lien externe */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">
                Ou un lien externe
              </p>
              <div className="space-y-2">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">URL</label>
                  <Input
                    placeholder="leboncoin.fr ou https://chatgpt.com"
                    value={externalUrl}
                    onChange={(e) => { setExternalUrl(e.target.value); setExternalError(null); }}
                    onKeyDown={(e) => { if (e.key === "Enter") handleAddExternalShortcut(); }}
                    className="text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">
                    Nom du raccourci <span className="opacity-60">(facultatif)</span>
                  </label>
                  <Input
                    placeholder="Ex : Boncoin, ChatGPT, Gmail…"
                    value={externalLabel}
                    onChange={(e) => setExternalLabel(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleAddExternalShortcut(); }}
                    maxLength={20}
                    className="text-sm"
                  />
                  <p className="text-[10px] text-muted-foreground/70 mt-1">
                    Court de préférence (max 20 caractères) — sera affiché sous l'icône.
                  </p>
                </div>
                {externalError && (
                  <p className="text-xs text-destructive">{externalError}</p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShortcutDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleAddExternalShortcut} disabled={!externalUrl.trim()}>
              Ajouter le lien externe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
